import Banner from 'components/Banner'
import Loader from 'components/Icons/LoadingSpinner'
import ApeModeQueryParamReader from 'hooks/useApeModeQueryParamReader'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'
import { Z_INDEX } from 'theme/zIndex'

import { useAnalyticsReporter } from '../components/Analytics'
import ErrorBoundary from '../components/ErrorBoundary'
import Header from '../components/Header'
import { PageTabs } from '../components/NavBar'
import Polling from '../components/Polling'
import Popups from '../components/Popups'
import DarkModeQueryParamReader from '../theme/components/DarkModeQueryParamReader'
import { RedirectDuplicateTokenIds } from './AddLiquidity/redirects'
import { RedirectPathToSwapOnly } from './Swap/redirects'
import { RedirectInvalidToken } from './TokenDetails/redirects'

const SwapPage = lazy(() => import('./Swap'))
// const SwapLimit = lazy(() => import('./Trade/SwapLimit'))
const PoolPage = lazy(() => import('./Pool'))
const PositionPage = lazy(() => import('./Pool/PositionPage'))
const RemoveLiquidityPage = lazy(() => import('./RemoveLiquidity'))
const NotFoundPage = lazy(() => import('./NotFound'))
const LiquidityPage = lazy(() => import('./Liquidity'))
const AddLiquidityPage = lazy(() => import('./AddLiquidity'))
const Strategy = lazy(() => import('./Strategy/Details'))
const StrategiesOverview = lazy(() => import('./Strategy/Overview'))
const Dashboard = lazy(() => import('./Dashboard'))
const DashboardGMI = lazy(() => import('./DashboardGMI'))
const LiquidityDetails = lazy(() => import('./LiquidityDetails'))
const StrategiesView = lazy(() => import('./Strategy/Positions'))
const GmiPage = lazy(() => import('./GmiPage'))
const WagmiVesting = lazy(() => import('./WagmiVesting'))

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  padding: ${({ theme }) => theme.navHeight}px 0px 5rem 0px;
  align-items: center;
  flex: 1;
`

const MobileBottomBar = styled.div`
  z-index: ${Z_INDEX.sticky};
  position: fixed;
  display: flex;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100vw;
  justify-content: space-between;
  padding: 4px 8px;
  backdrop-filter: blur(30px);
  height: ${({ theme }) => theme.mobileBottomBarHeight}px;
  background: ${({ theme }) => theme.navbarBackground};
  border-top: 1px solid ${({ theme }) => theme.backgroundOutline};

  @media screen and (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    display: none;
  }
`

const LoaderWrapper = styled.div`
  height: ${({ theme }) => `calc(100vh - ${theme.navHeight}px)`};
  width: 100wh;
  display: flex;
  align-items: center;
  justify-content: center;
`

export default function App() {
  const { pathname } = useLocation()
  const [scrolledState, setScrolledState] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)
    setScrolledState(false)
  }, [pathname])

  useEffect(() => {
    const scrollListener = () => {
      setScrolledState(window.scrollY > 0)
    }
    window.addEventListener('scroll', scrollListener)
    return () => window.removeEventListener('scroll', scrollListener)
  }, [])

  const isHeaderTransparent = !scrolledState

  useAnalyticsReporter()

  return (
    <ErrorBoundary>
      <DarkModeQueryParamReader />
      <ApeModeQueryParamReader />
      <Header isHeaderTransparent={isHeaderTransparent} />
      <BodyWrapper>
        <Popups />
        <Polling />
        <Banner />
        <Suspense
          fallback={
            <LoaderWrapper>
              <Loader size="50px" />
            </LoaderWrapper>
          }
        >
          <Routes>
            <Route path="/" element={<RedirectPathToSwapOnly />} />

            <Route path="trade/swap" element={<SwapPage />} />
            {/* <Route path="trade/swap/limit" element={<SwapLimit />} /> */}
            <Route path="gmi" element={<GmiPage />} />
            <Route path="gmi/vesting" element={<WagmiVesting />} />

            <Route path="liquidity" element={<LiquidityPage />} />
            <Route path="liquidity/add" element={<RedirectDuplicateTokenIds />}>
              {/* this is workaround since react-router-dom v6 doesn't support optional parameters any more */}
              <Route path=":currencyIdA" />
              <Route path=":currencyIdA/:currencyIdB" />
              <Route path=":currencyIdA/:currencyIdB/:feeAmount" />
            </Route>

            <Route path="liquidity/increase" element={<AddLiquidityPage />}>
              <Route path=":currencyIdA" />
              <Route path=":currencyIdA/:currencyIdB" />
              <Route path=":currencyIdA/:currencyIdB/:feeAmount" />
              <Route path=":currencyIdA/:currencyIdB/:feeAmount/:tokenId" />
            </Route>

            <Route path="liquidity/details/:address" element={<LiquidityDetails />} />

            <Route path="liquidity/pool" element={<PoolPage />} />
            <Route path="liquidity/pool/:tokenId" element={<PositionPage />} />

            <Route path="liquidity/pools" element={<PoolPage />} />
            <Route path="liquidity/pools/:tokenId" element={<PositionPage />} />

            <Route path="liquidity/tokens/:address" element={<RedirectInvalidToken />} />

            <Route path="liquidity/remove/:tokenId" element={<RemoveLiquidityPage />} />

            <Route path="liquidity/strategies/:currencyIdA/:currencyIdB" element={<Strategy />} />
            <Route path="liquidity/strategies" element={<StrategiesOverview />} />

            <Route path="dashboard" element={<Dashboard />} />
            <Route path="dashboard/strategies" element={<StrategiesView />} />
            <Route path="dashboard/gmi" element={<DashboardGMI />} />

            <Route path="*" element={<Navigate to="/not-found" replace />} />
            <Route path="/not-found" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BodyWrapper>
      <MobileBottomBar>
        <PageTabs />
      </MobileBottomBar>
    </ErrorBoundary>
  )
}
